
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table'
import './SkuProjectsTable.css'

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return dateStr.substring(0, 10)
}

const ProductSubRow = ({ product, lang, t }) => {
  if (!product) return null
  return (
    <div className="subrow-product">
      <div className="subrow-header">{t('PRODUCT')}</div>
      <table className="subrow-table">
        <thead>
          <tr>
            <th>{t('NAME')}/{t('DESCRIPTION')}</th>
            <th>{t('SKU')}</th>
            <th>{t('BRAND_DESIGNATION') || t('BRAND') || 'Marque'}</th>
            <th>{t('LEVEL1') || t('Catégorie')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{
              product.briefDescription ? product.briefDescription : product.description ? product.description : '-'
            }</td>
            <td>{product.cupPC ? product.cupPC : (product.barcode1 ? product.barcode1 : '-')}</td>
            <td>{product.brandLabel ? product.brandLabel : product.productBrand ? product.productBrand : '-'}</td>
            <td>{product.categorie}</td>
          </tr>
        </tbody>
      </table>
      {product.briefDescription && (
        <div className="subrow-desc">{product.briefDescription.substring(0, 100)}</div>
      )}
    </div>
  )
}

const SkuProjectsTable = ({ projets }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'fr'
  const data = useMemo(() => projets, [projets])
  const columnHelper = createColumnHelper()
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: t('PROJECT_NAME'),
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('supplierName', {
      header: t('SUPPLIER_NAME'),
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('createdAt', {
      header: t('CREATE_AT'),
      cell: info => formatDate(info.getValue()),
    }),
  ], [columnHelper, t, lang])

  const table = useReactTable({
    data,
    columns,
    getSubRows: row => row.product ? [row.product] : [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  return (
    <div className="table-responsive react-table tableContainer">
      <table className="table table-hover table-bordered sku-table">
        <thead className="table-light table-nowrap sku-table-header">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className="align-middle sku-table-header-cell">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
              <th className="align-middle sku-table-header-cell"></th>
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr className={row.getIsExpanded() ? 'table-active' : ''}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
                <td className="align-middle text-center" style={{ width: 40 }}>
                  {row.getCanExpand() && row.original.product ? (
                    <span
                      className="expand-btn"
                      onClick={row.getToggleExpandedHandler()}
                      title={row.getIsExpanded() ? t('CLOSE') : t('SHOW')}
                    >
                      {row.getIsExpanded() ? '▼' : '▶'}
                    </span>
                  ) : null}
                </td>
              </tr>
              {row.getIsExpanded() && row.original.product && (
                <tr className="subrow-tr">
                  <td colSpan={row.getVisibleCells().length + 1}>
                    <ProductSubRow product={row.original.product} lang={lang} t={t} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

SkuProjectsTable.propTypes = {
  projets: PropTypes.array.isRequired,
}

export default SkuProjectsTable
